"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var helpers_1 = require("./helpers");
var filter_1 = require("./filter");
/**
 * calculate line of best fit and get slope, y-intercept, and r-squared
 *
 * @param {number:[]} data Array of values to process
 * @param {number} startIndex Index of array to start using for calculations (inclusive)
 * @param {number} endIndex Index of array to stop using for calculations (exclusive)
 *
 * @returns {Object} Object containing slope, yIntercept, and rSquared values
 */
function getLineOfBestFit(xs, ys, startIndex, endIndex) {
    if (startIndex + 1 >= endIndex || xs.length !== ys.length) {
        return {
            slope: 0.0,
            yIntercept: 0.0
        };
    }
    var count = 0;
    var sumX = 0;
    var sumY = 0;
    var sumXSquared = 0;
    var sumYSquared = 0;
    var sumXY = 0;
    var xMean = 0;
    var yMean = 0;
    for (var i = startIndex; i <= endIndex; i++) {
        count++;
        sumX += xs[i];
        sumY += ys[i];
        sumXSquared += xs[i] * xs[i];
        sumYSquared += ys[i] * ys[i];
        sumXY += xs[i] * ys[i];
    }
    xMean = sumX / count;
    yMean = sumY / count;
    var slope = (sumXY - sumX * yMean) / (sumXSquared - sumX * xMean);
    var yIntercept = yMean - slope * xMean;
    var r = ((count * sumXY) - (sumX * sumY)) / (Math.sqrt(((count * sumXSquared) - (sumX * sumX)) * ((count * sumYSquared) - (sumY * sumY))));
    var rSquared = r * r;
    return {
        slope: slope,
        yIntercept: yIntercept,
        rSquared: rSquared
    };
}
exports.getLineOfBestFit = getLineOfBestFit;
/**
 * calculate the point where a series of values crosses a specific value, and stays over that value
 *
 * @param {number[]} data List of values to use for crossing point calculation
 * @param {Number} threshold Value to compare data to
 * @param {Number} startIndex value of index to start looking for crossing point (inclusive)
 * @param {Number} endIndex value of index to stop looking fro crossing point (exclusive)
 *
 * @returns {Number} Floating point value of where data crossed the threshold, 0.0 indicates that no crossing point was determined
 */
function getCrossingPointIndex(data, threshold, startIndex, endIndex) {
    if (startIndex < 0 || endIndex > data.length) {
        throw "Start and End index must fall within the bounds of data";
    }
    if (startIndex === 0) {
        startIndex = 1;
    }
    var cq = 0.0;
    var clearOnUnder = false;
    var positiveCrossings = 0;
    for (var i = startIndex; i < endIndex; i++) {
        var crossThreshold = data[i] >= threshold && data[i - 1] < threshold;
        if (crossThreshold === true) {
            if (clearOnUnder === false) {
                clearOnUnder = true;
                var toAdd = (threshold - data[i - 1]) / (data[i] - data[i - 1]); // Fractional value of the crossing point
                cq = (i - 1) + toAdd;
                positiveCrossings += 1;
            }
        }
        else if (data[i] < threshold && clearOnUnder) {
            cq = 0.0;
            clearOnUnder = false;
        }
    }
    if ((cq < startIndex || cq > endIndex) && positiveCrossings > 1) {
        cq = 0.0;
    }
    return cq;
}
exports.getCrossingPointIndex = getCrossingPointIndex;
/**
 * subtract all values in data by the minimum of value of data
 *
 * @param {number[]} data array of numbers to apply minimum subtraction to
 *
 * @returns {Object} contains the minimumSubtractedData and the subtraction value
 */
function applyMinimumSubtraction(data, startIndex, endIndex) {
    var minimum = Math.min.apply(Math, data.slice(startIndex, endIndex));
    var minimumSubtractedArray = data.map(function (x) { return x - minimum; });
    return {
        minimumSubtractedArray: minimumSubtractedArray,
        subtractionValue: minimum
    };
}
exports.applyMinimumSubtraction = applyMinimumSubtraction;
/**
  * calculate the first derivative and the necessary values for hump detection
  *
  * @param {number[]} data array of numbers to calculate d1 from
  *
  * @return {Object} contains the array of d1 value, the d1Max value, the d1MaxIndex, and the integral of d1
  */
function determineD1Values(data) {
    var d1Data = helpers_1.derivative(data);
    var d1Max = Math.max.apply(Math, d1Data);
    var d1MaxIndex = d1Data.indexOf(d1Max);
    var d1Integral = helpers_1.integral(d1Data);
    return {
        d1Data: d1Data,
        d1Max: d1Max,
        d1MaxIndex: d1MaxIndex,
        d1Integral: d1Integral
    };
}
exports.determineD1Values = determineD1Values;
/**
 * take filtered, baselined data and calculate the threshold
 *
 * @param {number[]} data baselined and filtered data that will be presented
 * @param {number} thresholdScalar scalar to apply to the background standard deviation
 * @param {number} backgroundLeft value to use for the start of the background of data (inclusive)
 * @param {number} backgroundRight value to use for the end of the background of data (exclusive)
 *
 * @returns {Object} contains the background threshold, average, and standard deviation values
 */
function determineBackgroundThreshold(data, thresholdScalar, backgroundLeft, backgroundRight) {
    var background = data.slice(backgroundLeft, backgroundRight + 1);
    var backgroundStandardDeviation = helpers_1.standardDeviation(background);
    var backgroundAverage = helpers_1.average(helpers_1.absoluteValues(background));
    var threshold = backgroundAverage + (backgroundStandardDeviation * thresholdScalar);
    return {
        backgroundThreshold: threshold,
        backgroundAverage: backgroundAverage,
        backgroundStandardDeviation: backgroundStandardDeviation
    };
}
exports.determineBackgroundThreshold = determineBackgroundThreshold;
/**
 * apply an FIR filter to data
 *
 * @param  {number[]} data
 * @returns number[]
 */
function applyFIRFilter(data) {
    var coeffs = [0.02008337, 0.04016673]; //, 0.020083327];
    var FIR_FILTER_SCALAR = 16.5974828;
    var FIR_PADDING = 3;
    var paddedInput = Array.apply(undefined, Array(FIR_PADDING)).map(function (i) { return data[0]; }).concat(data, Array.apply(undefined, Array(FIR_PADDING)).map(function (i) { return data[data.length - 1]; }));
    var filteredInput = filter_1.fir_filter(paddedInput, coeffs).map(function (item) { return item * FIR_FILTER_SCALAR; }); //.slice(FIR_PADDING,-FIR_PADDING);
    var reveresedFilteredInput = filteredInput.reverse();
    var paddedReversedFilteredInput = reveresedFilteredInput.slice();
    var filteredReversedFilteredInput = filter_1.fir_filter(paddedReversedFilteredInput, coeffs).map(function (item) { return item * FIR_FILTER_SCALAR; }); //;
    var filteredData = filteredReversedFilteredInput.slice(FIR_PADDING, -FIR_PADDING).reverse();
    return filteredData;
}
exports.applyFIRFilter = applyFIRFilter;
/**
 * @param {number[]} data
 * @param {number} startIndex
 * @param {number} endIndex
 * @returns number[]
 */
function applyLOBFAdjustment(data, startIndex, endIndex) {
    if (startIndex < 0 || endIndex + 1 > data.length) {
        throw "Start and End index must fall within the bounds of data";
    }
    var cycles = Array.apply(undefined, Array(data.length)).map(function (x, i) { return i; });
    var slope = getLineOfBestFit(cycles, data, startIndex, endIndex).slope;
    var lobfAdjustedData = data.map(function (value, index) { return value - (index * slope); });
    return lobfAdjustedData;
}
exports.applyLOBFAdjustment = applyLOBFAdjustment;
/**
 * @param {number[]} data
 * @param {number} startIndex
 * @param {number} endIndex
 * @returns number[]
 */
function applyMeanBackgroundSubtraction(data, startIndex, endIndex) {
    if (startIndex < 0 || endIndex + 1 > data.length) {
        throw "Start and End index must fall within the bounds of data";
    }
    var mean = helpers_1.average(data.slice(startIndex, endIndex + 1));
    var meanSubtractedData = data.map(function (value) { return value - mean; });
    return meanSubtractedData;
}
exports.applyMeanBackgroundSubtraction = applyMeanBackgroundSubtraction;
/**
 * @param {number[]} data
 * @param {number} startIndex
 * @returns number
 */
function getDeltaRFU(input_data, startIndex) {
    var maxVal = 0;
    var minVal = 1E9;
    var data = input_data.slice();
    if (data.length - startIndex < 5) {
        var additionalPoints = 5 - (data.length - startIndex);
        for (var i = 0; i <= additionalPoints; i++) {
            data.push(data[data.length - 1]);
        }
    }
    var cycles = data.map(function (x, i) { return i; });
    for (var i = startIndex; i < cycles.length - 4; i++) {
        var avg = helpers_1.average(data.slice(i, i + 5));
        if (avg >= maxVal) {
            maxVal = avg;
        }
        if (avg < minVal) {
            minVal = avg;
        }
    }
    var deltaRFU = maxVal - minVal;
    return deltaRFU;
}
exports.getDeltaRFU = getDeltaRFU;
function determineNegativeD1Values(data) {
    var d1Data = helpers_1.derivative(data).map(function (i) { return -1 * i; });
    var d1Max = Math.max.apply(Math, d1Data);
    var d1MaxIndex = d1Data.indexOf(d1Max);
    return {
        d1Data: d1Data,
        d1MaxIndex: d1MaxIndex
    };
}
exports.determineNegativeD1Values = determineNegativeD1Values;
function determinePeakTemperature(d1Data, meltTemperatures) {
    var peakIndices = [];
    for (var i = 1; i < d1Data.length - 1; i++) {
        if (d1Data[i - 1] < d1Data[i] && d1Data[i + 1] < d1Data[i]) {
            peakIndices.push(i);
        }
    }
    var peakValues = peakIndices.map(function (x) {
        return d1Data[x];
    });
    var maxPeakIndex = d1Data.indexOf(Math.max.apply(Math, peakValues));
    return meltTemperatures[maxPeakIndex];
}
exports.determinePeakTemperature = determinePeakTemperature;
